/**
 * Safety state: who I muted, who I blocked, and who blocked me.
 *
 * Ids are account ids (auth user ids). People without an account (signed-out
 * visitors) can still be muted or blocked for this session by presence key,
 * stored as `k:<presenceKey>`; that never leaves this tab.
 *
 * Signed in: mutes/blocks live in user_mutes / user_blocks; who blocked me
 * comes from the get_safety_relations() RPC (ids only). Signed out: mutes and
 * blocks persist in localStorage.
 *
 * Plain functions (isBlocked, isHiddenUser, …) read the current state and work
 * anywhere (broadcast handlers, other features like world invites). Components
 * subscribe with useSafetyStore so changes apply without a reload.
 */
import { create } from 'zustand';

export type IdSet = Record<string, true>;

export interface SafetyState {
  /** Signed-in user id (null for visitors). */
  me: string | null;
  muted: IdSet;
  blocked: IdSet;
  /** Accounts that blocked me (mutual block: I hide them too). */
  blockedBy: IdSet;
  /** Display names we know for blocked/muted ids (Profile list, People list). */
  names: Record<string, string>;
  loaded: boolean;
  /** Bumped on every change; cheap dependency for memoised filters. */
  rev: number;
}

export const useSafetyStore = create<SafetyState>(() => ({
  me: null,
  muted: {},
  blocked: {},
  blockedBy: {},
  names: {},
  loaded: false,
  rev: 0,
}));

export const sessionKey = (presenceKey: string) => `k:${presenceKey}`;

const has = (set: IdSet, id?: string | null) => !!id && !!set[id];

// ---- Pure checks against a given state (use inside selectors) ----

export function blockedIn(s: SafetyState, userId?: string | null, presenceKey?: string | null) {
  return has(s.blocked, userId) || (!!presenceKey && has(s.blocked, sessionKey(presenceKey)));
}
/** Hidden from me: I blocked them, or they blocked me. */
export function hiddenIn(s: SafetyState, userId?: string | null, presenceKey?: string | null) {
  return blockedIn(s, userId, presenceKey) || has(s.blockedBy, userId);
}
/** Voice + chat suppressed: muted, or hidden by a block either way. */
export function mutedIn(s: SafetyState, userId?: string | null, presenceKey?: string | null) {
  return has(s.muted, userId) || (!!presenceKey && has(s.muted, sessionKey(presenceKey))) || hiddenIn(s, userId, presenceKey);
}

// ---- Helpers for other features (read the live state) ----

/** I blocked this account. */
export const isBlocked = (userId?: string | null, presenceKey?: string | null) => blockedIn(useSafetyStore.getState(), userId, presenceKey);
/** This account blocked me. */
export const hasBlockedMe = (userId?: string | null) => has(useSafetyStore.getState().blockedBy, userId);
/** Either side blocked: don't render, don't deliver invites/DMs/emotes. */
export const isHiddenUser = (userId?: string | null, presenceKey?: string | null) => hiddenIn(useSafetyStore.getState(), userId, presenceKey);
/** Mute or block: their voice and chat should not reach me. */
export const isMutedUser = (userId?: string | null, presenceKey?: string | null) => mutedIn(useSafetyStore.getState(), userId, presenceKey);
/**
 * World invites (and any other person-to-person request): drop it when either
 * side blocked the other. Use on the receiving side for incoming invites, and
 * on the sending side to leave such people out of the invite list.
 */
export const canInteract = (userId?: string | null, presenceKey?: string | null) => !isHiddenUser(userId, presenceKey);

export const setSafety = (patch: Partial<SafetyState>) =>
  useSafetyStore.setState((s) => ({ ...patch, rev: s.rev + 1 }));
